import { cleanUndefined } from '@/src/utils';
import { api } from 'dicomweb-client-typed';
import { globalHeaders } from '@/src/utils/fetch';
import { isMobileBrowser } from '@/src/utils/mobileVolume';
import { TAG_TO_NAME, Tags } from '@/src/core/dicomTags';

export interface FetchStudyOptions {
  studyInstanceUID: string;
}

export interface FetchSeriesOptions extends FetchStudyOptions {
  seriesInstanceUID: string;
  sopInstanceUIDs?: string[];
}

export interface FetchInstanceOptions extends FetchSeriesOptions {
  sopInstanceUID: string;
}

type FetchInstanceWithProgressOptions = Omit<
  FetchInstanceOptions,
  'sopInstanceUIDs'
> & {
  mediaTypes?: Array<{
    mediaType: string;
    transferSyntaxUID?: string;
  }>;
  progressCallback: (event: ProgressEvent) => void;
};

const tags = {
  PatientID: '00100020',
  PatientName: '00100010',
  PatientBirthDate: '00100030',
  PatientSex: '00100040',

  StudyID: '00200010',
  StudyInstanceUID: '0020000D',
  StudyName: '00100010',
  StudyDate: '00080020',
  StudyTime: '00080030',
  AccessionNumber: '00080050',
  StudyDescription: '00081030',

  SeriesInstanceUID: '0020000E',
  SeriesNumber: '00200011',
  SeriesDescription: '0008103E',
  Modality: '00080060',
  WindowLevel: '00281050',
  WindowWidth: '00281051',

  SopInstanceUID: '00080018',
  InstanceNumber: '00200013',

  Rows: '00280010',
  Columns: '00280011',
};

export type Instance = typeof tags & { NumberOfFrames?: string };

function parseTag(value: any) {
  const v = value?.Value?.[0];
  const alpha = v?.Alphabetic;
  if (alpha) return alpha;
  return v;
}

function parseInstance(instance: any) {
  const withNamedTags = Object.entries({
    ...tags,
    NumberOfFrames: '00280008',
  }).reduce(
    (info, [key, tag]) => ({ ...info, [key]: parseTag(instance[tag]) }),
    {}
  );
  return cleanUndefined(withNamedTags) as Instance;
}

// Create unique file names so loader utils work
let fileCounter = 0;
function toFile(instance: ArrayBuffer) {
  fileCounter++;
  return new File([instance], `dicom-web.${fileCounter}.dcm`, {
    type: 'application/dicom',
  });
}

const SERIES_DOWNLOAD_CONCURRENCY = 6;
const PROGRESS_UPDATE_INTERVAL_MS = 100;
const JPEG_LOSSLESS_TRANSFER_SYNTAX = '1.2.840.10008.1.2.4.70';
const LOSSLESS_DICOM_MEDIA_TYPES = [
  {
    mediaType: 'application/dicom',
    transferSyntaxUID: JPEG_LOSSLESS_TRANSFER_SYNTAX,
  },
];

function makeClient(dicomWebRoot: string) {
  const headers = Object.fromEntries(globalHeaders.entries());
  return new api.DICOMwebClient({
    url: dicomWebRoot,
    headers,
  });
}

export async function searchForStudies(dicomWebRoot: string) {
  const client = makeClient(dicomWebRoot);
  const instances = await client.searchForStudies();
  return instances.map(parseInstance);
}

export async function retrieveStudyMetadata(
  dicomWebRoot: string,
  options: FetchStudyOptions
) {
  const client = makeClient(dicomWebRoot);
  const instances = await client.searchForSeries(options);
  return instances.map(parseInstance);
}

export async function retrieveSeriesMetadata(
  dicomWebRoot: string,
  options: FetchSeriesOptions
) {
  const client = makeClient(dicomWebRoot);
  const instances = await client.retrieveSeriesMetadata(options);
  return instances.map(parseInstance);
}

export interface DicomWebInstanceMetadata {
  sopInstanceUID: string;
  tags: Array<[string, string]>;
}

export function orderVolumeInstanceMetadata(
  instances: DicomWebInstanceMetadata[]
) {
  if (instances.length < 2) return instances;
  const first = new Map(instances[0].tags);
  const orientation = first
    .get(Tags.ImageOrientationPatient)
    ?.split('\\')
    .map(Number);
  if (
    orientation?.length !== 6 ||
    orientation.some((v) => !Number.isFinite(v))
  ) {
    return instances;
  }
  const [ax, ay, az, bx, by, bz] = orientation;
  const normal = [ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx];
  if (Math.hypot(...normal) < 0.5) return instances;

  const positioned = instances.map((instance) => {
    const meta = new Map(instance.tags);
    if (
      meta.get(Tags.ImageOrientationPatient) !==
      first.get(Tags.ImageOrientationPatient)
    ) {
      return null;
    }
    const position = meta
      .get(Tags.ImagePositionPatient)
      ?.split('\\')
      .map(Number);
    if (position?.length !== 3 || position.some((v) => !Number.isFinite(v))) {
      return null;
    }
    return {
      instance,
      distance: position.reduce(
        (sum, value, index) => sum + value * normal[index],
        0
      ),
    };
  });
  if (positioned.some((item) => item === null)) return instances;
  return (
    positioned as Array<{
      instance: DicomWebInstanceMetadata;
      distance: number;
    }>
  )
    .sort((a, b) => a.distance - b.distance)
    .map(({ instance }) => instance);
}

// DICOMweb metadata contains the tags needed to allocate a volume without
// holding every instance file (or copying them into the sorting WASM task).
export function parseVolumeInstanceMetadata(
  instance: object
): DicomWebInstanceMetadata {
  const raw = instance as Record<string, { Value?: unknown[] }>;
  const parsedTags: Array<[string, string]> = [];
  for (const [key] of TAG_TO_NAME) {
    const value = raw[key.replace('|', '').toUpperCase()]?.Value;
    if (!value?.length) continue;
    const parsed = value.map((item) => {
      if (typeof item === 'object' && item !== null) {
        return String((item as { Alphabetic?: string }).Alphabetic ?? '');
      }
      return String(item);
    });
    if (parsed.every(Boolean)) parsedTags.push([key, parsed.join('\\')]);
  }
  const tagMap = new Map(parsedTags);
  return {
    sopInstanceUID: tagMap.get(Tags.SOPInstanceUID) ?? '',
    tags: parsedTags,
  };
}

export async function retrieveVolumeInstanceMetadata(
  dicomWebRoot: string,
  options: FetchSeriesOptions
) {
  const client = makeClient(dicomWebRoot);
  const instances = await client.retrieveSeriesMetadata(options);
  return instances.map(parseVolumeInstanceMetadata);
}

export async function fetchInstanceData(
  dicomWebRoot: string,
  options: FetchInstanceOptions
): Promise<Blob> {
  const client = makeClient(dicomWebRoot);
  const retrieve = client.retrieveInstance.bind(client) as (
    instanceOptions: Omit<FetchInstanceWithProgressOptions, 'progressCallback'>
  ) => Promise<ArrayBuffer>;
  let buffer: ArrayBuffer;
  try {
    buffer = await retrieve({
      ...options,
      mediaTypes: LOSSLESS_DICOM_MEDIA_TYPES,
    });
  } catch {
    buffer = await retrieve(options);
  }
  return new Blob([buffer], { type: 'application/dicom' });
}

export async function fetchSeries(
  dicomWebRoot: string,
  options: FetchSeriesOptions,
  progressCallback: (n: ProgressEvent) => void
): Promise<File[]> {
  const client = makeClient(dicomWebRoot);
  // dicomweb-client supports this callback at runtime, but its retrieveInstance
  // TypeScript declaration omits it.
  const retrieveInstanceWithProgress = client.retrieveInstance.bind(client) as (
    instanceOptions: FetchInstanceWithProgressOptions
  ) => Promise<ArrayBuffer>;
  const { sopInstanceUIDs: providedInstanceUIDs, ...seriesOptions } = options;

  // The metadata is normally already available to the caller. Keep this
  // fallback so fetchSeries also works when it is used independently.
  let sopInstanceUIDs = providedInstanceUIDs;
  if (!sopInstanceUIDs?.length) {
    const metadata = await client.retrieveSeriesMetadata(seriesOptions);
    sopInstanceUIDs = metadata
      .map(parseInstance)
      .map(({ SopInstanceUID }) => SopInstanceUID)
      .filter(Boolean);
  }

  if (!sopInstanceUIDs.length) return [];

  const files = new Array<File>(sopInstanceUIDs.length);
  const instanceProgress = sopInstanceUIDs.map(() => ({ loaded: 0, total: 0 }));
  const downloadWorkerCount = Math.min(
    isMobileBrowser() ? 2 : SERIES_DOWNLOAD_CONCURRENCY,
    sopInstanceUIDs.length
  );
  const downloadStride = Math.ceil(
    sopInstanceUIDs.length / downloadWorkerCount
  );
  const downloadOrder = Array.from(
    { length: downloadStride * downloadWorkerCount },
    (_, orderIndex) => {
      const lane = orderIndex % downloadWorkerCount;
      const offset = Math.floor(orderIndex / downloadWorkerCount);
      return lane * downloadStride + offset;
    }
  ).filter((instanceIndex) => instanceIndex < sopInstanceUIDs.length);
  let nextDownloadOrderIndex = 0;
  let firstError: unknown;
  let lastProgressUpdate = 0;

  const emitProgress = (force = false) => {
    const now = Date.now();
    if (!force && now - lastProgressUpdate < PROGRESS_UPDATE_INTERVAL_MS) {
      return;
    }
    lastProgressUpdate = now;

    const loaded = instanceProgress.reduce(
      (sum, progress) => sum + progress.loaded,
      0
    );
    const knownTotals = instanceProgress
      .map(({ total }) => total)
      .filter((total) => total > 0);
    const estimatedTotal = knownTotals.length
      ? Math.round(
          (knownTotals.reduce((sum, total) => sum + total, 0) /
            knownTotals.length) *
            sopInstanceUIDs.length
        )
      : 0;
    const total = Math.max(loaded, estimatedTotal);

    progressCallback(
      new ProgressEvent('progress', {
        lengthComputable: total > 0,
        loaded,
        total,
      })
    );
  };

  const downloadWorker = async () => {
    while (!firstError) {
      const instanceIndex = downloadOrder[nextDownloadOrderIndex++];
      if (instanceIndex === undefined) return;

      const sopInstanceUID = sopInstanceUIDs[instanceIndex];
      try {
        const retrieveInstance = (preferLossless: boolean) =>
          retrieveInstanceWithProgress({
            ...seriesOptions,
            sopInstanceUID,
            ...(preferLossless
              ? { mediaTypes: LOSSLESS_DICOM_MEDIA_TYPES }
              : {}),
            progressCallback: ({ loaded, total }: ProgressEvent) => {
              instanceProgress[instanceIndex] = { loaded, total };
              emitProgress();
            },
          });

        let instance: ArrayBuffer;
        try {
          instance = await retrieveInstance(true);
        } catch {
          // Some DICOMweb servers cannot transcode every SOP class. Retry only
          // this instance using its original transfer syntax; one failure must
          // not disable lossless delivery for the rest of the series.
          instanceProgress[instanceIndex] = { loaded: 0, total: 0 };
          emitProgress(true);
          instance = await retrieveInstance(false);
        }

        const byteLength = instance.byteLength;
        instanceProgress[instanceIndex] = {
          loaded: byteLength,
          total: byteLength,
        };
        files[instanceIndex] = toFile(instance);
        emitProgress();
      } catch (error) {
        firstError ??= error;
      }
    }
  };

  await Promise.all(
    Array.from({ length: downloadWorkerCount }, () => downloadWorker())
  );

  if (firstError) throw firstError;
  emitProgress(true);
  return files;
}

export async function fetchInstanceThumbnail(
  dicomWebRoot: string,
  instance: FetchInstanceOptions
) {
  const client = makeClient(dicomWebRoot);
  const thumbnail = await client.retrieveInstanceRendered({
    ...instance,
    mediaTypes: [{ mediaType: 'image/jpeg' }],
  });
  const arrayBufferView = new Uint8Array(thumbnail);
  const blob = new Blob([arrayBufferView], { type: 'image/jpeg' });
  return URL.createObjectURL(blob);
}

const LEVELS = ['series', 'studies'] as const;

// takes a url like http://localhost:3000/dicom-web/studies/someid/series/anotherid
// returns { host: 'http://localhost:3000/dicom-web', studies: 'someid', series: 'anotherid' }
export function parseUrl(deepDicomWebUrl: string) {
  // remove trailing slash
  const sansSlash = deepDicomWebUrl.replace(/\/$/, '');

  let paths = sansSlash.split('/');
  const parentIDs = LEVELS.reduce((idObj, dicomLevel) => {
    const [urlLevel, dicomID] = paths.slice(-2);
    if (urlLevel === dicomLevel) {
      paths = paths.slice(0, -2);
      return { [dicomLevel]: dicomID, ...idObj };
    }
    return idObj;
  }, {});

  const pathsToSlice = Object.keys(parentIDs).length * 2;
  const allPaths = sansSlash.split('/');
  const host = allPaths.slice(0, allPaths.length - pathsToSlice).join('/');

  return { host, ...parentIDs };
}
